import React, { useMemo, useState } from 'react';
import { Modal, TextInput, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faMagnifyingGlass, faMinus } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { ItemsReportOptionItem } from 'shared-models/browser';
import type { ProductView } from '../../../../../services/products/products.service';
import type { CategoryView } from '../../../../../services/categories/categories.service';
import { Button } from '../../../../../components/Button';
import navyModal from '../../../../../components/Modal/navy-modal.module.scss';
import classes from './pos-items-report.module.scss';

export type PosItemsProductPickerModalProps = {
  opened: boolean;
  products: ProductView[];
  categories: CategoryView[];
  optionItems: ItemsReportOptionItem[];
  selectedProductIds: number[];
  selectedOptionItemIds: number[];
  onClose: () => void;
  onConfirm: (productIds: number[], optionItemIds: number[]) => void;
};

type Group = {
  key: string;
  title: string;
  kind: 'product' | 'option';
  items: Array<{ id: number; label: string }>;
};

type BoxState = 'on' | 'off' | 'mixed';

// iOS types a curly ’ where Android types ', and Hebrew names mix those with the geresh ׳.
// Comparing letters and digits only makes every spelling of צ'ייסר match the same product.
const searchable = (value: string): string => value.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

const withIds = (current: number[], ids: number[], on: boolean): number[] =>
  on ? Array.from(new Set([...current, ...ids])) : current.filter((id) => !ids.includes(id));

// Summary: דוח פריטים picker — a categories rail beside the active category's items; sizes and combo
// components ride along as two extra rail groups.
const PosItemsProductPickerModalComponent: React.FC<PosItemsProductPickerModalProps> = ({
  opened,
  products,
  categories,
  optionItems,
  selectedProductIds,
  selectedOptionItemIds,
  onClose,
  onConfirm,
}) => {
  const [search, setSearch] = useState('');
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [productDraft, setProductDraft] = useState<number[]>(selectedProductIds);
  const [optionDraft, setOptionDraft] = useState<number[]>(selectedOptionItemIds);

  // Reopening must start from what the report currently filters on, not the last abandoned draft.
  React.useEffect(() => {
    if (opened) {
      setProductDraft(selectedProductIds);
      setOptionDraft(selectedOptionItemIds);
      setSearch('');
      setSelectedOnly(false);
      setActiveKey(null);
    }
  }, [opened, selectedProductIds, selectedOptionItemIds]);

  const groups = useMemo<Group[]>(() => {
    // Seeding from the category list keeps the rail in menu order.
    const byKey = new Map<string, Group>(categories.map((category) => [
      `c-${category.id}`,
      { key: `c-${category.id}`, title: category.name, kind: 'product', items: [] },
    ]));
    for (const product of products) {
      const key = product.categoryId === null ? 'none' : `c-${product.categoryId}`;
      const group = byKey.get(key) ?? { key, title: 'ללא קטגוריה', kind: 'product', items: [] };
      group.items.push({ id: product.id, label: product.name });
      byKey.set(key, group);
    }
    const optionGroup = (key: string, title: string, kind: ItemsReportOptionItem['kind']): Group => ({
      key,
      title,
      kind: 'option',
      items: optionItems.filter((option) => option.kind === kind).map((option) => ({
        id: option.itemId,
        label: option.kind === 'variant' && option.productName
          ? `${option.productName} (${option.itemName})`
          : option.itemName,
      })),
    });
    return [...byKey.values(), optionGroup('variants', 'גדלים', 'variant'), optionGroup('combos', 'מרכיבי דילים', 'combo')]
      .filter((group) => group.items.length > 0);
  }, [categories, products, optionItems]);

  const draftOf = (kind: Group['kind']) => (kind === 'product' ? productDraft : optionDraft);
  const setMany = (kind: Group['kind'], ids: number[], on: boolean) =>
    (kind === 'product' ? setProductDraft : setOptionDraft)((current) => withIds(current, ids, on));

  const term = searchable(search);
  const visibleGroups = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        (!term || searchable(group.title).includes(term) || searchable(item.label).includes(term))
        && (!selectedOnly || draftOf(group.kind).includes(item.id))),
    }))
    .filter((group) => group.items.length > 0);
  const active = visibleGroups.find((group) => group.key === activeKey) ?? visibleGroups[0] ?? null;

  const selectedIn = (group: Group) => group.items.filter((item) => draftOf(group.kind).includes(item.id)).length;
  const stateOf = (group: Group): BoxState => {
    const count = selectedIn(group);
    return count === 0 ? 'off' : count === group.items.length ? 'on' : 'mixed';
  };
  // Bulk actions touch only what is on screen, so a search never changes hidden items.
  const applyToVisible = (on: boolean) =>
    visibleGroups.forEach((group) => setMany(group.kind, group.items.map((item) => item.id), on));

  const totalSelected = productDraft.length + optionDraft.length;
  const totalItems = groups.reduce((sum, group) => sum + group.items.length, 0);

  const renderBox = (state: BoxState) => (
    <span className={state === 'off' ? classes.pickerBox : `${classes.pickerBox} ${classes.pickerBoxOn}`}>
      {state !== 'off' && <FontAwesomeIcon icon={(state === 'on' ? faCheck : faMinus) as IconProp} />}
    </span>
  );

  const segment = (on: boolean, label: string, testId: string, onClick: () => void) => (
    <UnstyledButton
      component="button"
      type="button"
      data-testid={testId}
      aria-pressed={on}
      className={on ? `${classes.pickerSegmentButton} ${classes.pickerSegmentActive}` : classes.pickerSegmentButton}
      onClick={onClick}
    >
      {label}
    </UnstyledButton>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<strong className={classes.pickerTitle}>בחירת פריטים</strong>}
      size="xl"
      centered
      radius="md"
      dir="rtl"
      zIndex={500}
      closeButtonProps={{ 'data-testid': 'items-report-picker-close' } as Record<string, string>}
      classNames={{ root: navyModal.root, body: classes.pickerBody }}
    >
      <TextInput
        data-testid="items-report-picker-search"
        placeholder="חיפוש מוצר או קטגוריה..."
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
        leftSection={<FontAwesomeIcon icon={faMagnifyingGlass as IconProp} />}
      />

      <div className={classes.pickerToolbar}>
        <div className={classes.pickerSegment}>
          {segment(!selectedOnly, 'כל הפריטים', 'items-report-picker-show-all', () => setSelectedOnly(false))}
          {segment(selectedOnly, `נבחרים בלבד (${totalSelected})`, 'items-report-picker-show-selected', () => setSelectedOnly(true))}
        </div>
        <div className={classes.pickerActions}>
          <UnstyledButton component="button" type="button" className={classes.pickerBulk} data-testid="items-report-picker-select-all" onClick={() => applyToVisible(true)}>
            בחר הכל
          </UnstyledButton>
          <UnstyledButton component="button" type="button" className={classes.pickerBulk} data-testid="items-report-picker-clear-all" onClick={() => applyToVisible(false)}>
            נקה הכל
          </UnstyledButton>
        </div>
      </div>

      <div className={classes.pickerPanes}>
        <nav className={classes.pickerRail} aria-label="קטגוריות">
          <span className={`${classes.pickerMuted} ${classes.pickerRailLabel}`}>קטגוריות</span>
          {visibleGroups.map((group) => {
            const state = stateOf(group);
            return (
              <div key={group.key} className={group.key === active?.key ? `${classes.pickerRailRow} ${classes.pickerRailRowActive}` : classes.pickerRailRow}>
                <UnstyledButton
                  component="button"
                  type="button"
                  aria-label={group.title}
                  aria-pressed={state === 'on'}
                  className={classes.pickerRailBox}
                  data-testid={`items-report-picker-category-${group.key}`}
                  onClick={() => setMany(group.kind, group.items.map((item) => item.id), state !== 'on')}
                >
                  {renderBox(state)}
                </UnstyledButton>
                <UnstyledButton
                  component="button"
                  type="button"
                  className={classes.pickerRailName}
                  data-testid={`items-report-picker-rail-${group.key}`}
                  onClick={() => setActiveKey(group.key)}
                >
                  <span>{group.title}</span>
                  <span className={classes.pickerMuted}>{`${selectedIn(group)}/${group.items.length}`}</span>
                </UnstyledButton>
              </div>
            );
          })}
        </nav>

        <section className={classes.pickerPane}>
          {active ? (
            <>
              <div className={classes.pickerPaneHead}>
                <strong>{active.title}</strong>
                <span className={classes.pickerMuted}>{`${active.items.length} פריטים · ${selectedIn(active)} נבחרו`}</span>
              </div>
              <div className={classes.pickerGrid}>
                {active.items.map((item) => {
                  const on = draftOf(active.kind).includes(item.id);
                  return (
                    <UnstyledButton
                      key={item.id}
                      component="button"
                      type="button"
                      aria-pressed={on}
                      data-testid={`items-report-picker-${active.kind}-${item.id}`}
                      className={on ? `${classes.pickerTile} ${classes.pickerTileSelected}` : classes.pickerTile}
                      onClick={() => setMany(active.kind, [item.id], !on)}
                    >
                      {renderBox(on ? 'on' : 'off')}
                      <span>{item.label}</span>
                    </UnstyledButton>
                  );
                })}
              </div>
            </>
          ) : (
            <div className={classes.pickerEmpty}>לא נמצאו פריטים</div>
          )}
        </section>
      </div>

      <div className={classes.pickerFooter}>
        <div className={classes.pickerSummary}>
          <strong>{`${totalSelected} פריטים נבחרו`}</strong>
          <span className={classes.pickerMuted}>{`מתוך ${totalItems}`}</span>
        </div>
        <div className={classes.pickerActions}>
          <Button variant="cancel" onClick={onClose} dataTestId="items-report-picker-cancel">
            ביטול
          </Button>
          <Button
            variant="confirm"
            onClick={() => onConfirm(productDraft, optionDraft)}
            disabled={totalSelected === 0}
            dataTestId="items-report-picker-confirm"
          >
            {`אישור (${totalSelected})`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const PosItemsProductPickerModal = React.memo(PosItemsProductPickerModalComponent);
